import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { TextField, Button, Modal, Box, IconButton } from '@mui/material';
import * as yup from 'yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../../../core/hooks';
import { useIdentityStore } from '../../../../../core/store';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    maxWidth: '400px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    overflowY: 'auto',
    '@media (max-width: 600px)': {
        width: '90%',
        p: 2,
    },
};

const PasswordForm = ({ idUser, verifyPasswordBackend }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState('');
    const [isPasswordVerified, setIsPasswordVerified] = useState(false);
    const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [init, setInit] = useState(false);

    useEffect(() => {
        setInit(true);
    }, []);

    const handleOpen = () => {
        setCurrentPassword('');
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const { updatePassword } = useUser();
    const { identity } = useIdentityStore();

    const passwordSchema = yup.object().shape({
        currentPassword: yup.string().required(t('screens.user.formPassword.validation.required')),
        newPassword: yup
            .string()
            .min(4, t('screens.user.formPassword.validation.minLength'))
            .required(t('screens.user.formPassword.validation.required')),
        confirmPassword: yup
            .string()
            .oneOf([yup.ref('newPassword')], t('screens.user.formPassword.validation.passwordMismatch'))
            .required(t('screens.user.formPassword.validation.required')),
    });

    const verifyPassword = async (password) => {
        try {
            console.log('idUser', idUser);
            console.log('password', password);
            const isValid = await verifyPasswordBackend(idUser, password);
            console.log('isValid', isValid);
            return isValid || false;
        } catch (e) {
            console.error(e);
            return false;
        }
    };

    useEffect(() => {
        if (currentPassword.length > 0) {
            const delayDebounce = setTimeout(async () => {
                const result = await verifyPassword(currentPassword);
                setIsPasswordVerified(true);
                setIsPasswordCorrect(result);
            }, 500);

            return () => clearTimeout(delayDebounce);
        } else {
            setIsPasswordVerified(false);
        }
    }, [currentPassword]);

    if (!init) {
        return null;
    }

    return (
        <>
            <div className="flex justify-between items-center py-2 border-y mb-4">
                <span className="text-slate-700 font-semibold">{t('screens.user.formPassword.labels.currentPassword')}</span>
                <div className="flex items-center">
                    <span className="mr-2 font-mono">*************</span>
                    <button
                        type="button"
                        onClick={handleOpen}
                        className="text-slate-500 hover:text-slate-800 font-semibold"
                    >
                        {t('screens.user.formPassword.buttons.edit')}
                    </button>
                </div>
            </div>
            <Modal open={isOpen} onClose={handleClose}>
                <Box sx={modalStyle} className="rounded-xl">
                    <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium">{t('screens.user.formPassword.modalTitle')}</p>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <Formik
                        initialValues={{
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                        }}
                        validationSchema={passwordSchema}
                        onSubmit={async (values, { resetForm }) => {
                            try {
                                const idUserFromIdentity = identity.idUser;
                                const res = await updatePassword(idUser, values.newPassword, idUserFromIdentity);
                                if (res.ok) {
                                    console.log('Usuario actualizado correctamente');
                                } else {
                                    navigate('/');
                                }
                            } catch (error) {
                                console.error(error);
                                navigate('/');
                            } finally {
                                handleClose();
                                resetForm();
                            }
                        }}
                    >
                        {({ isSubmitting, touched, values, setFieldValue, errors }) => (
                            <Form className="space-y-4">
                                <Field
                                    variant="standard"
                                    as={TextField}
                                    name="currentPassword"
                                    type="password"
                                    label={t('screens.user.formPassword.labels.currentPassword')}
                                    fullWidth
                                    margin="normal"
                                    disabled={isPasswordVerified && isPasswordCorrect}
                                    error={!!values.currentPassword && !isPasswordVerified}
                                    helperText={<ErrorMessage name="currentPassword" />}
                                    InputProps={{
                                        endAdornment: isPasswordVerified ? (
                                            isPasswordCorrect ? (
                                                <CheckCircleOutlineIcon style={{ color: 'green' }} />
                                            ) : (
                                                <ErrorOutlineIcon style={{ color: 'red' }} />
                                            )
                                        ) : null,
                                        readOnly: isPasswordVerified && isPasswordCorrect,
                                    }}
                                    value={currentPassword}
                                    onChange={(e) => {
                                        setIsPasswordVerified(false);
                                        setCurrentPassword(e.target.value);
                                        setFieldValue('currentPassword', e.target.value);
                                    }}
                                />
                                <Field
                                    variant="standard"
                                    as={TextField}
                                    name="newPassword"
                                    type="password"
                                    label={t('screens.user.formPassword.labels.newPassword')}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isPasswordVerified || !isPasswordCorrect}
                                    error={touched.newPassword && !!errors.newPassword}
                                    helperText={touched.newPassword && errors.newPassword ? errors.newPassword : ''}
                                />
                                <Field
                                    variant="standard"
                                    as={TextField}
                                    name="confirmPassword"
                                    type="password"
                                    label={t('screens.user.formPassword.labels.confirmPassword')}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isPasswordVerified || !isPasswordCorrect}
                                    error={touched.confirmPassword && !!errors.confirmPassword}
                                    helperText={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
                                />
                                <div className="flex justify-end space-x-4">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                        className="mt-2"
                                    >
                                        {t('screens.user.formPassword.buttons.save')}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </>
    );
};

export default PasswordForm;
